import React from "react";
import {
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { labelSx, fieldSx } from "./authStyles";

/**
 * AuthField
 * Shared auth input: an external label above a MUI TextField (the SignIn
 * pattern), an optional start-adornment icon, an optional password show/hide
 * toggle, and helperText that always reserves space so the layout does not
 * shift when an error appears.
 *
 * Props:
 * - label: text rendered above the field
 * - startIcon: optional node placed in a start InputAdornment
 * - password: when true, renders a show/hide IconButton and toggles the type
 * - showPassword / onTogglePassword: controlled state for the toggle
 * - toggleAriaLabel: aria-label for the toggle button
 * - error: error message string (also flips the field into the error state)
 * - labelProps: extra sx merged into the label (e.g. top margin)
 * - ...rest: forwarded to the underlying MUI TextField
 */
export default function AuthField({
  label,
  startIcon = null,
  password = false,
  showPassword = false,
  onTogglePassword,
  toggleAriaLabel = "toggle password visibility",
  error = "",
  type,
  labelProps,
  slotProps = {},
  ...rest
}) {
  const resolvedType = password ? (showPassword ? "text" : "password") : type;

  const inputProps = {
    ...(startIcon
      ? {
          startAdornment: (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ),
        }
      : {}),
    ...(password
      ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={onTogglePassword}
                edge="end"
                size="small"
                aria-label={toggleAriaLabel}
              >
                {showPassword ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }
      : {}),
  };

  return (
    <>
      {label ? (
        <Typography sx={{ ...labelSx, ...labelProps }}>{label}</Typography>
      ) : null}
      <TextField
        fullWidth
        type={resolvedType}
        error={!!error}
        // Reserve a line of space so the layout stays stable with/without errors.
        helperText={error || " "}
        sx={fieldSx}
        slotProps={{
          ...slotProps,
          input: (ownerState) => ({
            ...(typeof slotProps.input === "function"
              ? slotProps.input(ownerState)
              : slotProps.input),
            ...inputProps,
          }),
        }}
        {...rest}
      />
    </>
  );
}
